import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { API_BASE_URL } from '@/config/api';
import { DEV_CONSOLE_AUTH_REDIRECT_WINDOW_MS, DEV_CONSOLE_MAX_AUTH_REDIRECTS } from '@/config/devConsole';

interface AuthUser {
  email: string;
  name: string;
  sub: string;
  groups?: string[];
  pods?: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  error: null,
  signOut: () => {
    throw new Error('signOut is not implemented');
  },
});

export const useAuth = () => useContext(AuthContext);

export function buildLoginUrl(location: Pick<Location, 'hash' | 'pathname' | 'search'>): string {
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  const params = new URLSearchParams({ returnTo });
  return `${API_BASE_URL}/auth/login?${params}`;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
      .then((res) => {
        if (res.ok) return res.json();
        return null;
      })
      .then((data) => {
        if (data) {
          setUser(data);
          setLoading(false);
          sessionStorage.removeItem('auth_redirect_count');
          sessionStorage.removeItem('auth_redirect_ts');
        } else {
          redirectToLogin();
        }
      })
      .catch(() => {
        redirectToLogin();
      });

    function redirectToLogin() {
      const now = Date.now();
      const lastTs = Number(sessionStorage.getItem('auth_redirect_ts') ?? '0');
      const count = Number(sessionStorage.getItem('auth_redirect_count') ?? '0');

      // Reset counter if outside the rapid-redirect window
      const effectiveCount = now - lastTs > DEV_CONSOLE_AUTH_REDIRECT_WINDOW_MS ? 0 : count;

      if (effectiveCount >= DEV_CONSOLE_MAX_AUTH_REDIRECTS) {
        setLoading(false);
        setError('Authentication failed after multiple attempts. Please clear your cookies and try again.');
        sessionStorage.removeItem('auth_redirect_count');
        sessionStorage.removeItem('auth_redirect_ts');
        return;
      }

      sessionStorage.setItem('auth_redirect_count', String(effectiveCount + 1));
      sessionStorage.setItem('auth_redirect_ts', String(now));
      window.location.href = buildLoginUrl(window.location);
    }
  }, []);

  const signOut = useCallback(() => {
    fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' })
      .then((res) => res.json())
      .then((data: { ok: boolean; logoutUrl: string | null }) => {
        setUser(null);
        if (data.logoutUrl) {
          window.location.href = data.logoutUrl;
        } else {
          window.location.href = '/';
        }
      })
      .catch(() => {
        setUser(null);
        window.location.href = '/';
      });
  }, []);

  if (error) {
    return (
      <div className='flex h-screen flex-col items-center justify-center gap-4 p-6'>
        <p className='text-sm text-neutral-600'>{error}</p>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            sessionStorage.removeItem('auth_redirect_count');
            sessionStorage.removeItem('auth_redirect_ts');
            window.location.href = buildLoginUrl(window.location);
          }}
          className='rounded-md bg-orange-500 px-4 py-2 text-sm text-white hover:bg-orange-600'
        >
          Try again
        </button>
      </div>
    );
  }

  return <AuthContext.Provider value={{ user, loading, error, signOut }}>{children}</AuthContext.Provider>;
};
